import type { ReactElement } from 'react';
import { useCallback, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { DataTable } from '@/components/DataTable';
import type { DataFetchParams } from '@/components/DataTable';
import { TABLE_IDS } from '@/components/DataTable/tableIds';
import { SortOrder } from '@kirin/api-client/contracts/helpers';
import {
  AUDIT_LOGS_FILTERABLE_FIELDS,
  type AuditLog,
} from '@kirin/api-client/contracts/audit-logs-table';
import { useSecurityGroupsIncludingDeleted } from '@/hooks/api/useSecurityGroups';
import { useCanViewAdminContent } from '@/hooks/useRoleCheck';
import { useFeatureFlags } from '@/hooks/useFeatureFlags';
import { flagsmithFeatureFlags } from '@/lib/flagsmith';
import { createAuditLogsColumns } from './columns';
import { fetchOrganizationAuditLogs } from './api';

function getQueryKeysFromParams(
  organizationId: string,
  params: DataFetchParams
): readonly unknown[] {
  return [
    'auditLogs',
    'dataTable',
    {
      organizationId,
      page: params.pagination.pageIndex + 1,
      limit: params.pagination.pageSize,
      filters: params.filters,
      sortBy: params.sort?.field,
      sortOrder: params.sort?.order,
      search: params.search,
    },
  ];
}

/** Organization audit-logs DataTable, keyed per organization to reset state. */
export function DataTableAuditLogsContent({
  organizationId,
}: {
  organizationId: string;
}): ReactElement {
  // KIR-4701: name the security group a change was scoped to. The groups
  // endpoint is admin-only and exists only with the flag on.
  const { flags } = useFeatureFlags([
    flagsmithFeatureFlags.SECURITY_GROUPS_POLICIES,
  ]);
  const { canViewAdminContent } = useCanViewAdminContent(organizationId);
  // Deleted groups included: a change keeps naming the group it was made for.
  const { data: securityGroups } = useSecurityGroupsIncludingDeleted(
    organizationId,
    (flags[flagsmithFeatureFlags.SECURITY_GROUPS_POLICIES] ?? false) &&
      canViewAdminContent
  );
  const groupNames = useMemo(
    () =>
      securityGroups
        ? new Map(
            securityGroups.map(group => [
              group.id,
              group.deletedAt ? `${group.name} (deleted)` : group.name,
            ])
          )
        : undefined,
    [securityGroups]
  );

  const columns = useMemo(
    () => createAuditLogsColumns(organizationId, groupNames),
    [organizationId, groupNames]
  );

  const queryFn = useCallback(
    (params: DataFetchParams, signal?: AbortSignal) =>
      fetchOrganizationAuditLogs(organizationId, params, signal),
    [organizationId]
  );

  const queryKeyFn = useCallback(
    (params: DataFetchParams) => getQueryKeysFromParams(organizationId, params),
    [organizationId]
  );

  // Include organizationId in countScope so knownTotal cache is invalidated
  // when the user navigates between organizations.
  const countScope = useMemo(() => ({ organizationId }), [organizationId]);

  return (
    <DataTable<AuditLog>
      id={TABLE_IDS.ORGANIZATION.AUDIT_LOGS}
      mode="server"
      className="flex flex-col h-[calc(100vh-11rem)]"
      containerClassName="flex-1"
      columns={columns}
      search={{
        placeholder: 'Search audit logs',
        className: 'w-[260px]',
        inputProps: { 'aria-label': 'Search audit logs' },
      }}
      features={{ enableSorting: true }}
      initialState={{
        sort: {
          field: AUDIT_LOGS_FILTERABLE_FIELDS.TIMESTAMP,
          order: SortOrder.Desc,
        },
      }}
      queryFn={queryFn}
      getQueryKeysFromParams={queryKeyFn}
      countScope={countScope}
      noDataLabel="No audit logs found."
      errorBoundaryLabel="Something went wrong in the Audit Logs table"
      queryConfig={{ staleTime: 30000, refetchOnWindowFocus: false }}
    />
  );
}

DataTableAuditLogsContent.displayName = 'DataTableAuditLogsContent';

export default function DataTableAuditLogsTab(): ReactElement | null {
  const { organizationId } = useParams<{ organizationId: string }>();

  if (!organizationId) {
    return null;
  }

  return (
    <DataTableAuditLogsContent
      key={organizationId}
      organizationId={organizationId}
    />
  );
}
